import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { JVInput } from "../../shared/JVInput";
import { JVDropdownInput } from "../../shared/JVDropdownInput";
import { TVendorCompanyRecord, VendorTypeOptions } from "./modal";
import { TDropdown } from "../../shared/modal";
import { Fetch } from "../../utilities/fetch";
import { JVDropdown } from "../../shared/JVDropdown";
import { useToast } from "../../context/toastProvider";

const schema = yup.object({
    name: yup.string().required("VendorCompany Name is required*").min(3),
    address: yup.string().required("Address is required*").min(3),
    countryId: yup.number().min(1, "Country is required*").required("Country is required*"),
    city: yup.object().shape({
        id: yup.number().min(1, "City is required*").required("City is required*"),
        name: yup.string().required()
    }),
    state: yup.object().shape({
        id: yup.number().min(1, "State is required*").required("State is required*"),
        name: yup.string().required()
    }),
    vendorType: yup.number().required(),
    phoneNo: yup.string().required("Phone Number is required*")
        .matches(/^[0-9-+() ]*$/, "Phone Number can only contain numbers, spaces, and - + ( ) characters")
        .min(7, "Phone Number must be at least 7 digits").max(15, "Phone Number cannot exceed 15 digits"),
    websiteUrl: yup.string().required("VendorCompany Domain is required").matches(/^(?!https?:\/\/)[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        "Enter a valid domain like abc.com (Do not include http:// or https://)"),
});

interface Iprops {
    data: TVendorCompanyRecord,
    open: boolean,
    hideDialog: () => void,
    reload: () => void,
}

export const Form = (props: Iprops) => {
    const { showToast } = useToast();
    const [countries, setCountries] = useState<Array<TDropdown>>();
    const [states, setStates] = useState<Array<TDropdown>>();
    const [cities, setCities] = useState<Array<TDropdown>>();

    const [loading, setLoading] = useState(false);

    const { control, handleSubmit, formState: { errors }, reset, watch } = useForm<TVendorCompanyRecord>({
        resolver: yupResolver(schema),
        defaultValues: props.data,
        shouldFocusError: false
    });

    useEffect(() => {
        reset(props.data);
    }, [props.data]);

    const loadCountries = async () => {
        const data = await Fetch<TDropdown[]>(`dropdown/countries`);
        if (data) {
            setCountries(data);
        }
    }

    const countryId = watch('countryId');
    const state = watch('state');

    const loadStates = async (filter: string | null) => {
        if (countryId) {
            const data = await Fetch<TDropdown[]>("dropdown/states",
                { query: `countryId=${countryId}&filter=${filter}` });
            if (data) {
                setStates(data);
            }
        } else {
            showToast({ severity: 'error', detail: "Select the Country First.", life: 3000 });
        }
    }

    const loadCities = async (filter: string | null) => {
        if (state?.id) {
            const data = await Fetch<TDropdown[]>("dropdown/cities", {
                query: `stateId=${state.id}&filter=${filter}`
            });
            if (data) {
                setCities(data);
            }
        } else {
            showToast({ severity: 'error', detail: "Select the State First.", life: 3000 });
        }
    }


    const completeMethodState = (event: { query: string }) => {
        loadStates(event.query);
    }

    const completeMethodCity = (event: { query: string }) => {
        loadCities(event.query);
    }

    useEffect(() => {
        loadCountries();
    }, []);

    const onHide = () => {
        props.hideDialog();
    }

    const onSubmit = async (data: TVendorCompanyRecord) => {
        if (loading) return;
        setLoading(true);

        const id = props.data.id;
        const method = id ? "PUT" : "POST";
        const message = id ? "Updated Successfully" : "Added Successfully";

        data.cityId = data.city?.id;

        const response = await Fetch(id ? `vendorCompanies/${id}` : 'vendorCompanies', {
            method,
            body: data,
            onError: (m) => showToast({ severity: 'error', detail: m })
        });

        if (response) {
            props.hideDialog();
            props.reload();
            showToast({ severity: 'success', detail: message });
        }

        setLoading(false);
    }

    return (
        <Dialog visible={props.open} style={{ width: '80vw' }} header="Add/Edit Vendor Company" modal className="p-fluid" onHide={onHide}>
            <form onSubmit={handleSubmit(onSubmit)} className="p-fluid formgrid grid">

                <JVInput column={2} required label="Vendor Company Name" control={control} name="name" error={errors.name?.message} />
                <JVInput column={2} label="FEID" control={control} name="feid" error={errors.feid?.message} />
                <JVInput column={2} required label="Address" control={control} name="address" error={errors.address?.message} />
                <JVDropdown required label="Vendor Type" control={control} name="vendorType" error={errors.vendorType?.message}
                    options={VendorTypeOptions} optionLabel="name" optionValue="id" placeholder="Select Work From" />

                <JVDropdown required column={3} label="Country" control={control} name="countryId" optionLabel="name" optionValue="id" options={countries} placeholder="Select Country" error={errors.countryId?.message} />
                <JVDropdownInput required column={3} completeMethod={completeMethodState} label="State" control={control} name="state" error={errors.state?.id?.message} options={states} optionLabel="name" placeholder="Search State" />
                <JVDropdownInput required column={3} completeMethod={completeMethodCity} label="City" control={control} name="city" error={errors.city?.id?.message} options={cities} optionLabel="name" placeholder="Search City" />

                <JVInput label="Phone No" required column={2} control={control} name="phoneNo" error={errors.phoneNo?.message} />
                <JVInput label="Alternate Phone No" column={2} control={control} name="alternatePhoneNo" error={errors.alternatePhoneNo?.message} />
                <JVInput label="Website URL" required column={2} control={control} name="websiteUrl" error={errors.websiteUrl?.message} />
                <JVInput label="Major Technology" column={2} control={control} name="majorTech" error={errors.majorTech?.message} />
                <JVInput label="FaxNo" column={2} control={control} name="faxNo" error={errors.faxNo?.message} />

                <div className="col-12 md:col-12 flex gap-4 justify-content-end">
                    <Button style={{ width: '100px' }} label="SAVE" type="submit" loading={loading}></Button>
                    <Button style={{ width: '100px' }} label="CLOSE" onClick={onHide} type="button" className="p-button-outlined surface-200"></Button>
                </div>

            </form>
        </Dialog>
    );
}
